import { useEffect, useMemo, useState } from 'react';
import { usePage } from '@inertiajs/react';
import {
    ChevronDown,
    ChevronRight,
    Headset,
    Menu,
    PanelLeftClose,
    PanelLeftOpen,
    X,
} from 'lucide-react';
import { SearchField } from '../forms/SearchField';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '../ui/Collapsible';
import NexusNavLink from '../navigation/NexusNavLink';
import { ScrollArea } from '../ui/ScrollArea';
import { cn } from '../ui/utils';
import { resolveMenuIcon, type NavItem } from '../../data/menu';
import { useLayoutPreferences } from '../../hooks/useLayoutPreferences';
import { resolveTopNavOrder } from '../../hooks/layoutPreferencesStorage';
import { normalizePath, pathMatches as pathMatchesShared } from '../../lib/menuPath';
import type { PageProps } from '../../types';

type AppSidebarProps = {
    collapsed: boolean;
    onToggleCollapsed: () => void;
    isMobile?: boolean;
    mobileOpen?: boolean;
    onCloseMobile?: () => void;
    onToggleMobile?: () => void;
};

function currentPath(): string {
    if (typeof window === 'undefined') {
        return '';
    }
    return normalizePath(window.location.pathname || '/');
}

function pathMatches(href: string | null | undefined, path: string): boolean {
    return pathMatchesShared(href, path);
}

function itemIsActive(item: NavItem, path: string): boolean {
    if (pathMatches(item.href, path)) {
        return true;
    }
    return Boolean(item.children?.some((child) => pathMatches(child.href, path)));
}

function normalizeText(text: string): string {
    return text
        .toLocaleLowerCase('pt-BR')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
}

function filterMenu(items: NavItem[], query: string): NavItem[] {
    const q = normalizeText(query.trim());
    if (!q) {
        return items;
    }

    return items
        .map((item) => {
            const labelMatch = normalizeText(item.label).includes(q);
            const children = item.children?.filter((child) =>
                normalizeText(child.label).includes(q),
            );

            if (labelMatch) {
                return item;
            }

            if (children && children.length > 0) {
                return { ...item, children };
            }

            return null;
        })
        .filter((item): item is NavItem => item !== null);
}

export default function AppSidebar({
    collapsed,
    onToggleCollapsed,
    isMobile = false,
    mobileOpen = false,
    onCloseMobile,
    onToggleMobile,
}: AppSidebarProps) {
    const page = usePage<PageProps>();
    const rawMenu = page.props.menu ?? [];
    const path = currentPath();
    const { topNavOrder } = useLayoutPreferences();

    const menu = useMemo(
        () => resolveTopNavOrder(rawMenu, topNavOrder),
        [rawMenu, topNavOrder],
    );

    const [filter, setFilter] = useState('');

    const initialOpen = useMemo(() => {
        const open: Record<string, boolean> = {};
        for (const item of menu) {
            if (itemIsActive(item, path) && item.children?.length) {
                open[item.id] = true;
            }
        }
        return open;
    }, [menu, path]);

    const [openSections, setOpenSections] = useState<Record<string, boolean>>(initialOpen);

    useEffect(() => {
        setOpenSections((prev) => {
            const next = { ...prev };
            let changed = false;
            for (const item of menu) {
                if (
                    itemIsActive(item, path) &&
                    item.children?.length &&
                    !next[item.id]
                ) {
                    next[item.id] = true;
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
    }, [menu, path]);

    const filteredItems = useMemo(
        () => filterMenu(menu, filter),
        [menu, filter],
    );

    const showLabels = !collapsed;

    return (
        <aside
            id="nx-sidebar"
            className={cn(
                'nx-sidebar',
                collapsed && 'nx-sidebar--collapsed',
                isMobile && 'nx-sidebar--mobile',
                isMobile && mobileOpen && 'nx-sidebar--mobile-open',
            )}
            aria-label="Menu lateral"
            aria-hidden={isMobile && !mobileOpen ? true : undefined}
            inert={isMobile && !mobileOpen ? true : undefined}
        >
            <div className="nx-sidebar-top">
                <div className="nx-brand">
                    <div className="nx-brand-identity">
                        <img
                            src="/logo_infor.png"
                            alt="Infor"
                            className="nx-brand-logo"
                            width={32}
                            height={32}
                        />
                        {showLabels && (
                            <span className="nx-brand-text">SNGPC WEB INFOR</span>
                        )}
                    </div>
                    <button
                        type="button"
                        className="nx-icon-btn nx-icon-btn--ghost nx-sidebar-menu-btn"
                        aria-label={
                            isMobile
                                ? mobileOpen
                                    ? 'Fechar menu'
                                    : 'Abrir menu'
                                : collapsed
                                  ? 'Expandir menu'
                                  : 'Alternar menu'
                        }
                        onClick={isMobile ? onToggleMobile : onToggleCollapsed}
                    >
                        {isMobile && mobileOpen ? (
                            <X size={18} strokeWidth={2} />
                        ) : (
                            <Menu size={18} strokeWidth={2} />
                        )}
                    </button>
                </div>

                {showLabels && (
                    <SearchField
                        className="nx-menu-filter"
                        size="sm"
                        label="Filtrar menu"
                        placeholder="Filtrar menu"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        autoComplete="off"
                    />
                )}
            </div>

            <ScrollArea className="nx-sidebar-scroll">
                <nav className="nx-nav" aria-label="Navegação principal">
                    {filteredItems.length === 0 ? (
                        showLabels && (
                            <p className="nx-nav-empty">
                                {filter.trim()
                                    ? 'Nenhum item encontrado'
                                    : 'Nenhum menu disponível'}
                            </p>
                        )
                    ) : null}

                    {filteredItems.map((item) => {
                        const Icon = resolveMenuIcon(item.icon);
                        const hasChildren = Boolean(item.children?.length);
                        const isOpen =
                            openSections[item.id] ??
                            (filter.trim() ? true : false);
                        const isActiveSection = itemIsActive(item, path);

                        if (!hasChildren) {
                            return (
                                <NexusNavLink
                                    key={item.id}
                                    href={item.href ?? '#'}
                                    target={item.target ?? undefined}
                                    className={cn(
                                        'nx-nav-item',
                                        isActiveSection && 'nx-nav-item--active',
                                    )}
                                    title={collapsed ? item.label : undefined}
                                    onClick={isMobile ? onCloseMobile : undefined}
                                >
                                    <Icon size={17} strokeWidth={1.85} />
                                    {showLabels && <span>{item.label}</span>}
                                </NexusNavLink>
                            );
                        }

                        if (collapsed) {
                            const firstChild = item.children?.[0];
                            return (
                                <NexusNavLink
                                    key={item.id}
                                    href={firstChild?.href ?? '#'}
                                    className={cn(
                                        'nx-nav-item',
                                        isActiveSection && 'nx-nav-item--active',
                                    )}
                                    title={item.label}
                                >
                                    <Icon size={17} strokeWidth={1.85} />
                                </NexusNavLink>
                            );
                        }

                        return (
                            <Collapsible
                                key={item.id}
                                open={isOpen}
                                onOpenChange={(open) =>
                                    setOpenSections((prev) => ({
                                        ...prev,
                                        [item.id]: open,
                                    }))
                                }
                            >
                                <CollapsibleTrigger asChild>
                                    <button
                                        type="button"
                                        className={cn(
                                            'nx-nav-item nx-nav-item--parent',
                                            isActiveSection &&
                                                'nx-nav-item--section-active',
                                        )}
                                    >
                                        <Icon size={17} strokeWidth={1.85} />
                                        <span>{item.label}</span>
                                        {isOpen ? (
                                            <ChevronDown
                                                className="nx-nav-chevron"
                                                size={15}
                                            />
                                        ) : (
                                            <ChevronRight
                                                className="nx-nav-chevron"
                                                size={15}
                                            />
                                        )}
                                    </button>
                                </CollapsibleTrigger>
                                <CollapsibleContent
                                    forceMount
                                    className="nx-nav-children"
                                    inert={isOpen ? undefined : true}
                                >
                                    <div className="nx-nav-children-inner">
                                        <div className="nx-nav-children-body">
                                            {item.children?.map((child) => {
                                                const active = pathMatches(
                                                    child.href,
                                                    path,
                                                );
                                                return (
                                                    <NexusNavLink
                                                        key={child.id}
                                                        href={child.href}
                                                        target={
                                                            child.target ??
                                                            undefined
                                                        }
                                                        className={cn(
                                                            'nx-nav-subitem',
                                                            active &&
                                                                'nx-nav-subitem--active',
                                                        )}
                                                        onClick={
                                                            isMobile
                                                                ? onCloseMobile
                                                                : undefined
                                                        }
                                                    >
                                                        {child.label}
                                                    </NexusNavLink>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </CollapsibleContent>
                            </Collapsible>
                        );
                    })}
                </nav>
            </ScrollArea>

            <div className="nx-sidebar-footer">
                {showLabels && (
                    <a href="#" className="nx-support-card">
                        <span className="nx-support-icon">
                            <Headset size={18} strokeWidth={1.9} />
                        </span>
                        <span>
                            <strong>Suporte Online</strong>
                            <small>Fale com nossa equipe</small>
                        </span>
                    </a>
                )}

                {!isMobile && (
                    <button
                        type="button"
                        className="nx-collapse-toggle"
                        onClick={onToggleCollapsed}
                    >
                        {collapsed ? (
                            <PanelLeftOpen size={16} />
                        ) : (
                            <PanelLeftClose size={16} />
                        )}
                        {showLabels && <span>Recolher menu</span>}
                    </button>
                )}
            </div>
        </aside>
    );
}
